import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Upload, FileSpreadsheet, Image as ImageIcon, Loader2, CheckCircle2, XCircle, Calendar, HelpCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { BACKEND_URL, API_URL } from '../config/api';

const API = API_URL;

const BulkImportDialog = ({ open, onOpenChange, onSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [extractedBookings, setExtractedBookings] = useState([]);
  const [importMethod, setImportMethod] = useState(null); // 'screenshot', 'csv', or 'fareharbor'
  const [fareHarborUrl, setFareHarborUrl] = useState("");
  const [showFareHarborHelp, setShowFareHarborHelp] = useState(false);

  const handleFileUpload = async (event, method) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setImportMethod(method);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const endpoint = method === "screenshot" 
        ? `${API}/trips/extract-from-image`
        : `${API}/trips/parse-csv`;

      const response = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.success && response.data.trips.length > 0) {
        setExtractedBookings(response.data.trips);
        toast.success(`Extracted ${response.data.count} trips!`);
      } else {
        toast.error("No trips found in the file");
        setExtractedBookings([]);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error(`Failed to process file: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleBulkImport = async () => {
    if (extractedBookings.length === 0) {
      toast.error("No trips to import");
      return;
    }

    setUploading(true);
    try {
      const response = await axios.post(`${API}/trips/bulk`, {
        trips: extractedBookings.map(trip => ({
          date: trip.date,
          startTime: trip.startTime,
          duration: trip.duration || 60,
          notes: `${trip.contactName ? `Contact: ${trip.contactName}` : ''}${trip.bookingType ? ` | ${trip.bookingType}` : ''}${trip.notes ? ` | ${trip.notes}` : ''}`.trim()
        }))
      });

      toast.success(`Successfully imported ${response.data.created} trips!`);
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} trips had errors`);
      }

      setExtractedBookings([]);
      setImportMethod(null);
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error importing trips:", error);
      toast.error("Failed to import trips");
    } finally {
      setUploading(false);
    }
  };

  const handleFareHarborImport = async () => {
    if (!fareHarborUrl.trim()) {
      toast.error("Please enter your FareHarbor calendar URL");
      return;
    }

    setUploading(true);
    setImportMethod('fareharbor');
    
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.post(
        `${API}/trips/import-fareharbor`,
        { 
          ics_url: fareHarborUrl.trim(),
          weeks_back: 6,
          group_by_resource: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { imported, duplicates, original_trip_count, grouped_trip_count, asset_matching } = response.data;
        
        // Build success message
        let message = `✅ ${imported} trips imported!`;
        
        if (duplicates > 0) {
          message += ` (Skipped ${duplicates} duplicates)`;
        }
        
        if (original_trip_count && grouped_trip_count && original_trip_count !== grouped_trip_count) {
          const saved = original_trip_count - grouped_trip_count;
          message += `\n📦 Grouped ${original_trip_count} bookings into ${grouped_trip_count} trips`;
        }
        
        if (asset_matching) {
          if (asset_matching.matched > 0) {
            message += `\n🚤 Matched ${asset_matching.matched} assets`;
          }
          if (asset_matching.unmatched > 0) {
            message += `\n⚠️ ${asset_matching.unmatched} assets unmatched`;
          }
        }
        
        toast.success(message, { duration: 8000 });
        setFareHarborUrl("");
        onSuccess();
        onOpenChange(false);
      } else {
        toast.error("Import failed");
      }
    } catch (error) {
      console.error("FareHarbor import error:", error);
      
      // Extract detailed error message
      let errorMsg = "Failed to import from FareHarbor";
      
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          // Pydantic validation errors
          errorMsg = data.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (data.message) {
          errorMsg = data.message;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const resetImport = () => {
    setExtractedBookings([]);
    setImportMethod(null);
    setFareHarborUrl("");
    setShowFareHarborHelp(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Bulk Import Bookings</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {extractedBookings.length === 0 ? (
            <>
              {/* Import Method Selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Screenshot Upload */}
                <Card className="p-6 hover:shadow-lg transition-shadow duration-300 cursor-pointer border-2 border-dashed">
                  <label htmlFor="screenshot-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center space-y-3">
                      <div className="p-4 bg-cyan-100 rounded-full">
                        <ImageIcon className="w-8 h-8 text-cyan-600" />
                      </div>
                      <h3 className="font-semibold text-lg">Upload Screenshot</h3>
                      <p className="text-sm text-gray-600 text-center">
                        AI will extract trip data from your booking system screenshot
                      </p>
                      {uploading && importMethod === "screenshot" && (
                        <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
                      )}
                    </div>
                  </label>
                  <input
                    id="screenshot-upload"
                    data-testid="screenshot-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, "screenshot")}
                    className="hidden"
                    disabled={uploading}
                  />
                </Card>

                {/* CSV Upload */}
                <Card className="p-6 hover:shadow-lg transition-shadow duration-300 cursor-pointer border-2 border-dashed">
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center space-y-3">
                      <div className="p-4 bg-green-100 rounded-full">
                        <FileSpreadsheet className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="font-semibold text-lg">Upload CSV/Excel</h3>
                      <p className="text-sm text-gray-600 text-center">
                        Import trips from spreadsheet (date, startTime, duration columns)
                      </p>
                      {uploading && importMethod === "csv" && (
                        <Loader2 className="w-6 h-6 animate-spin text-green-600" />
                      )}
                    </div>
                  </label>
                  <input
                    id="csv-upload"
                    data-testid="csv-upload"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => handleFileUpload(e, "csv")}
                    className="hidden"
                    disabled={uploading}
                  />
                </Card>

                {/* FareHarbor Import */}
                <Card className="p-6 hover:shadow-lg transition-shadow duration-300 border-2 border-dashed">
                  <div className="flex flex-col items-center space-y-3">
                    <div className="p-4 bg-purple-100 rounded-full">
                      <Calendar className="w-8 h-8 text-purple-600" />
                    </div>
                    <h3 className="font-semibold text-lg">FareHarbor Calendar</h3>
                    <p className="text-sm text-gray-600 text-center">
                      Import trips from your FareHarbor booking calendar (last 6 weeks)
                    </p>
                    
                    <div className="w-full space-y-2 mt-2">
                      <Label className="text-xs">FareHarbor Calendar URL</Label>
                      <input
                        type="text"
                        value={fareHarborUrl}
                        onChange={(e) => setFareHarborUrl(e.target.value)}
                        placeholder="https://fareharbor.com/integrations/ics/..."
                        className="w-full px-3 py-2 text-sm border rounded-md"
                        disabled={uploading}
                      />
                      
                      <Button
                        onClick={handleFareHarborImport}
                        disabled={uploading || !fareHarborUrl.trim()}
                        className="w-full"
                        style={{background: '#9333ea'}}
                      >
                        {uploading && importMethod === "fareharbor" ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Calendar className="w-4 h-4 mr-2" />
                            Import from FareHarbor
                          </>
                        )}
                      </Button>
                      
                      <button
                        onClick={() => setShowFareHarborHelp(!showFareHarborHelp)}
                        className="w-full text-xs text-purple-600 hover:text-purple-800 flex items-center justify-center gap-1"
                      >
                        <HelpCircle className="w-3 h-3" />
                        How to get my FareHarbor URL?
                      </button>
                    </div>
                  </div>
                </Card>
              </div>

              {/* FareHarbor Help Box */}
              {showFareHarborHelp && (
                <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                  <div className="flex items-start gap-2">
                    <HelpCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-purple-900 mb-2">📚 Getting Your FareHarbor Calendar URL</h4>
                      <ol className="text-sm text-purple-800 space-y-2 list-decimal list-inside">
                        <li>Login to your FareHarbor dashboard</li>
                        <li>Navigate to: <span className="font-mono bg-white px-2 py-0.5 rounded">Settings → Integrations → Subscribe</span></li>
                        <li>Copy the ICS calendar URL (contains your token)</li>
                        <li>Paste it above and click "Import from FareHarbor"</li>
                      </ol>
                      
                      <div className="mt-3 p-3 bg-white rounded border border-purple-200">
                        <p className="text-xs text-purple-900 font-semibold mb-1">🔒 Security Notes:</p>
                        <ul className="text-xs text-purple-700 space-y-1 ml-4 list-disc">
                          <li><strong>We DO NOT store your token</strong> - it's only used for this one-time import</li>
                          <li>Only imports trips from the <strong>last 6 weeks</strong></li>
                          <li>Automatically <strong>skips duplicates</strong> on re-import</li>
                        </ul>
                      </div>
                      
                      <a
                        href="https://fareharbor.com/dashboard/settings/integrations/subscribe/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 mt-3 font-semibold"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open FareHarbor Integrations →
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Alternative: Use ChatGPT</h4>
                <p className="text-sm text-blue-800">
                  Screenshot your trips and ask ChatGPT to organize them in this format:
                </p>
                <pre className="mt-2 text-xs bg-white p-3 rounded border border-blue-200 overflow-x-auto">
{`date, startTime, duration
2025-10-20, 18:00, 120
2025-10-21, 19:00, 60`}
                </pre>
                <p className="text-sm text-blue-800 mt-2">
                  Then save as CSV and upload above!
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Preview Extracted Bookings */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    Extracted {extractedBookings.length} Bookings
                  </h3>
                  <Button variant="outline" size="sm" onClick={resetImport}>
                    Start Over
                  </Button>
                </div>

                <div className="max-h-96 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Date</th>
                        <th className="p-2 text-left">Time</th>
                        <th className="p-2 text-left">Duration</th>
                        <th className="p-2 text-left">Contact</th>
                        <th className="p-2 text-left">Type</th>
                        <th className="p-2 text-left">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extractedBookings.map((trip, index) => (
                        <tr key={index} className="border-t hover:bg-gray-50">
                          <td className="p-2">{trip.date}</td>
                          <td className="p-2">{trip.startTime}</td>
                          <td className="p-2">{trip.duration} min</td>
                          <td className="p-2">{trip.contactName || '-'}</td>
                          <td className="p-2 text-xs">{trip.bookingType || '-'}</td>
                          <td className="p-2 text-xs">{trip.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Import Button */}
              <div className="flex gap-3">
                <Button
                  data-testid="import-trips-button"
                  onClick={handleBulkImport}
                  disabled={uploading}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white py-6 text-lg"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2" />
                      Import {extractedBookings.length} Bookings
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportDialog;
